"use client";

/**
 * ActivitySignupModal
 * Modal for signing up for activity time slots or joining the waitlist
 */

import { useState, useEffect } from "react";
import { X, Clock, MapPin, Users, Loader2, CheckCircle, AlertCircle, Bell } from "lucide-react";
import { ActivityItem, ActivityTimeSlot, ActivityCustomField } from "@/features/landing-pages/_logic/types";

interface ActivitySignupModalProps {
  activity: ActivityItem;
  landingPageId: number;
  primaryColor: string;
  signupCounts: Record<string, number>; // time_slot_id -> count
  waitlistCounts?: Record<string, number>; // time_slot_id -> waitlist count
  userOnWaitlist?: Record<string, boolean>; // time_slot_id -> is user on waitlist
  collectDietaryRestrictions?: boolean;
  onClose: () => void;
  onSignupComplete: () => void;
}

interface FormData {
  name: string;
  email: string;
  time_slot_id: string;
  dietary_restrictions: string;
  custom_fields: Record<string, string | boolean>;
}

type SubmitState = "idle" | "submitting" | "success" | "error";
type ModalMode = "signup" | "waitlist";

export default function ActivitySignupModal({
  activity,
  landingPageId,
  primaryColor,
  signupCounts,
  waitlistCounts = {},
  userOnWaitlist = {},
  collectDietaryRestrictions,
  onClose,
  onSignupComplete,
}: ActivitySignupModalProps) {
  // States
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    time_slot_id: "",
    dietary_restrictions: "",
    custom_fields: {},
  });
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [mode, setMode] = useState<ModalMode>("signup");

  // Effects
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // Helpers
  const getAvailableSlots = () => {
    if (!activity.timeSlots) return [];
    return activity.timeSlots.filter((slot) => {
      const count = signupCounts[slot.id] || 0;
      return count < slot.capacity;
    });
  };

  const getSpotsRemaining = (slot: ActivityTimeSlot) => {
    const count = signupCounts[slot.id] || 0;
    return slot.capacity - count;
  };

  const isSlotFull = (slot: ActivityTimeSlot) => {
    return getSpotsRemaining(slot) <= 0;
  };

  const isUserOnWaitlistForSlot = (slotId: string) => {
    return userOnWaitlist[slotId] || false;
  };

  const formatTimeSlot = (slot: ActivityTimeSlot) => {
    let label = slot.time;
    if (slot.endTime) {
      label += ` - ${slot.endTime}`;
    }
    if (slot.date) {
      const dateStr = new Date(slot.date + "T00:00:00").toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
      label = `${dateStr}, ${label}`;
    }
    return label;
  };

  // Handlers
  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errorMessage) setErrorMessage("");
  };

  const handleCustomFieldChange = (fieldId: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      custom_fields: { ...prev.custom_fields, [fieldId]: value },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setErrorMessage("Please enter your name");
      return;
    }
    if (!formData.email.trim()) {
      setErrorMessage("Please enter your email");
      return;
    }
    if (!formData.time_slot_id) {
      setErrorMessage("Please select a time slot");
      return;
    }

    // Check required custom fields
    if (activity.customFields) {
      for (const field of activity.customFields) {
        if (field.required && !formData.custom_fields[field.id]) {
          setErrorMessage(`Please fill in ${field.label}`);
          return;
        }
      }
    }

    setSubmitState("submitting");
    setErrorMessage("");

    try {
      const selectedSlot = activity.timeSlots?.find((s) => s.id === formData.time_slot_id);

      const response = await fetch("/api/landing-pages/activity-signups", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landing_page_id: landingPageId,
          activity_id: activity.id,
          time_slot_id: formData.time_slot_id,
          name: formData.name,
          email: formData.email,
          dietary_restrictions: formData.dietary_restrictions || null,
          custom_fields: formData.custom_fields,
          activity_title: activity.title,
          time_slot_label: selectedSlot ? formatTimeSlot(selectedSlot) : null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.error || "Failed to sign up. Please try again.");
        setSubmitState("error");
        return;
      }

      setSubmitState("success");
      setTimeout(() => {
        onSignupComplete();
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Signup error:", error);
      setErrorMessage("An error occurred. Please try again.");
      setSubmitState("error");
    }
  };

  const handleJoinWaitlist = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setErrorMessage("Please enter your name");
      return;
    }
    if (!formData.email.trim()) {
      setErrorMessage("Please enter your email");
      return;
    }
    if (!formData.time_slot_id) {
      setErrorMessage("Please select a time slot");
      return;
    }

    setSubmitState("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("/api/landing-pages/activity-waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landing_page_id: landingPageId,
          activity_id: activity.id,
          time_slot_id: formData.time_slot_id,
          name: formData.name,
          email: formData.email,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.error || "Failed to join waitlist. Please try again.");
        setSubmitState("error");
        return;
      }

      setSubmitState("success");
      setTimeout(() => {
        onSignupComplete();
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Waitlist error:", error);
      setErrorMessage("An error occurred. Please try again.");
      setSubmitState("error");
    }
  };

  // Render
  const availableSlots = getAvailableSlots();
  const allSlotsFull = activity.timeSlots && activity.timeSlots.length > 0 && availableSlots.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-hidden">
      {/* Backdrop button for closing */}
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm touch-manipulation"
        onClick={onClose}
        aria-label="Close modal"
        tabIndex={-1}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 shrink-0">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900 pr-2 line-clamp-1">
            {mode === "waitlist" ? `Join Waitlist: ${activity.title}` : `Sign Up for ${activity.title}`}
          </h3>
          <button
            onClick={onClose}
            className="p-2.5 sm:p-2 rounded-full active:bg-gray-200 touch-manipulation min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center shrink-0"
            aria-label="Close modal"
            tabIndex={0}
          >
            <X className="w-6 h-6 sm:w-5 sm:h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 overscroll-contain">
          {/* Activity Info */}
          <div className="mb-5 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
            {activity.meetingLocation && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                <MapPin className="w-4 h-4" style={{ color: primaryColor }} />
                {activity.meetingLocation}
              </div>
            )}
            {activity.duration && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" style={{ color: primaryColor }} />
                {activity.duration}
              </div>
            )}
          </div>

          {/* Success State */}
          {submitState === "success" && mode === "signup" && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h4 className="text-xl font-semibold text-gray-900 mb-2">You're signed up!</h4>
              <p className="text-gray-600">Check your email for confirmation details.</p>
            </div>
          )}

          {/* Waitlist Success State */}
          {submitState === "success" && mode === "waitlist" && (
            <div className="text-center py-8">
              <Bell className="w-16 h-16 mx-auto mb-4" style={{ color: primaryColor }} />
              <h4 className="text-xl font-semibold text-gray-900 mb-2">You're on the waitlist!</h4>
              <p className="text-gray-600">We'll email you if a spot opens up.</p>
            </div>
          )}

          {/* All Slots Full State - Show waitlist option */}
          {allSlotsFull && submitState !== "success" && mode === "signup" && (
            <div className="text-center py-6">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
              <h4 className="text-lg font-semibold text-gray-900 mb-2">All time slots are full</h4>
              <p className="text-gray-600 mb-4">But you can join the waitlist to be notified if a spot opens up.</p>
              <button
                onClick={() => setMode("waitlist")}
                className="px-6 py-2.5 rounded-lg font-semibold text-white"
                style={{ backgroundColor: primaryColor }}
              >
                <Bell className="w-4 h-4 inline mr-2" />
                Join Waitlist
              </button>
            </div>
          )}

          {/* Form */}
          {!allSlotsFull && submitState !== "success" && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Time Slot Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select a Time Slot <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {activity.timeSlots?.map((slot) => {
                    const spotsRemaining = getSpotsRemaining(slot);
                    const isFull = spotsRemaining <= 0;
                    const isSelected = formData.time_slot_id === slot.id;

                    return (
                      <label
                        key={slot.id}
                        className={`flex items-center justify-between p-3.5 sm:p-3 border rounded-lg cursor-pointer touch-manipulation min-h-[52px] ${
                          isFull
                            ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-60"
                            : isSelected
                              ? "border-2 bg-opacity-10"
                              : "border-gray-200 active:bg-gray-50"
                        }`}
                        style={
                          isSelected && !isFull
                            ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` }
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="time_slot"
                            value={slot.id}
                            checked={isSelected}
                            disabled={isFull}
                            onChange={() => handleInputChange("time_slot_id", slot.id)}
                            className="w-5 h-5 sm:w-4 sm:h-4"
                            style={{ accentColor: primaryColor }}
                          />
                          <div>
                            <div className="font-medium text-gray-900 text-base sm:text-sm">{formatTimeSlot(slot)}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-sm">
                          <Users className="w-4 h-4 text-gray-400" />
                          {isFull ? (
                            <span className="text-red-500 font-medium">Full</span>
                          ) : (
                            <span className="text-gray-600">
                              {spotsRemaining} spot{spotsRemaining !== 1 ? "s" : ""} left
                            </span>
                          )}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm min-h-[48px] sm:min-h-0"
                  style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                  disabled={submitState === "submitting"}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm min-h-[48px] sm:min-h-0"
                  style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                  disabled={submitState === "submitting"}
                />
              </div>

              {/* Dietary Restrictions - show if global setting OR per-activity setting is enabled */}
              {(collectDietaryRestrictions || activity.collectDietaryRestrictions) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-1">Dietary Restrictions</label>
                  <input
                    type="text"
                    value={formData.dietary_restrictions}
                    onChange={(e) => handleInputChange("dietary_restrictions", e.target.value)}
                    placeholder="e.g., Vegetarian, Gluten-free, Allergies"
                    className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm min-h-[48px] sm:min-h-0"
                    style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                    disabled={submitState === "submitting"}
                  />
                </div>
              )}

              {/* Custom Fields */}
              {activity.customFields?.map((field: ActivityCustomField) => (
                <div key={field.id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>
                  {field.type === "text" && (
                    <input
                      type="text"
                      value={(formData.custom_fields[field.id] as string) || ""}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      placeholder={field.placeholder}
                      className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm min-h-[48px] sm:min-h-0"
                      style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                      disabled={submitState === "submitting"}
                    />
                  )}
                  {field.type === "textarea" && (
                    <textarea
                      value={(formData.custom_fields[field.id] as string) || ""}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      placeholder={field.placeholder}
                      rows={3}
                      className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm"
                      style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                      disabled={submitState === "submitting"}
                    />
                  )}
                  {field.type === "select" && field.options && (
                    <select
                      value={(formData.custom_fields[field.id] as string) || ""}
                      onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                      className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm min-h-[48px] sm:min-h-0"
                      style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                      disabled={submitState === "submitting"}
                    >
                      <option value="">Select an option</option>
                      {field.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  )}
                  {field.type === "checkbox" && (
                    <label className="flex items-center gap-3 cursor-pointer touch-manipulation min-h-[44px]">
                      <input
                        type="checkbox"
                        checked={!!formData.custom_fields[field.id]}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.checked)}
                        className="w-5 h-5 sm:w-4 sm:h-4 rounded"
                        style={{ accentColor: primaryColor }}
                        disabled={submitState === "submitting"}
                      />
                      <span className="text-gray-700">{field.placeholder || "Yes"}</span>
                    </label>
                  )}
                </div>
              ))}

              {/* Error Message */}
              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {errorMessage}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitState === "submitting"}
                className="w-full py-4 sm:py-3 px-5 rounded-lg font-semibold text-white text-base active:opacity-80 disabled:opacity-50 flex items-center justify-center gap-2 min-h-[52px] sm:min-h-0 mt-2"
                style={{
                  backgroundColor: primaryColor,
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {submitState === "submitting" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Signing up...
                  </>
                ) : (
                  "Sign Up"
                )}
              </button>
            </form>
          )}

          {/* Waitlist Form */}
          {mode === "waitlist" && submitState !== "success" && (
            <form onSubmit={handleJoinWaitlist} className="space-y-5">
              {/* Back button */}
              {!allSlotsFull && (
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                  ← Back to signup
                </button>
              )}

              {/* Time Slot Selection for Waitlist */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select a Time Slot to Join Waitlist <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {activity.timeSlots?.map((slot) => {
                    const isFull = isSlotFull(slot);
                    const alreadyOnWaitlist = isUserOnWaitlistForSlot(slot.id);
                    const isSelected = formData.time_slot_id === slot.id;

                    // Only show full slots in waitlist mode
                    if (!isFull) return null;

                    return (
                      <label
                        key={slot.id}
                        className={`flex items-center justify-between p-3.5 sm:p-3 border rounded-lg cursor-pointer touch-manipulation min-h-[52px] ${
                          alreadyOnWaitlist
                            ? "bg-gray-100 border-gray-200 cursor-not-allowed opacity-60"
                            : isSelected
                              ? "border-2 bg-opacity-10"
                              : "border-gray-200 active:bg-gray-50"
                        }`}
                        style={
                          isSelected && !alreadyOnWaitlist
                            ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` }
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="waitlist_time_slot"
                            value={slot.id}
                            checked={isSelected}
                            disabled={alreadyOnWaitlist}
                            onChange={() => handleInputChange("time_slot_id", slot.id)}
                            className="w-5 h-5 sm:w-4 sm:h-4"
                            style={{ accentColor: primaryColor }}
                          />
                          <div>
                            <div className="font-medium text-gray-900 text-base sm:text-sm">{formatTimeSlot(slot)}</div>
                            {alreadyOnWaitlist && (
                              <span className="text-xs text-green-600">You're on the waitlist</span>
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-red-500 font-medium">Full</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-1">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Your full name"
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm min-h-[48px] sm:min-h-0"
                  style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                  disabled={submitState === "submitting"}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 sm:mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-base sm:text-sm min-h-[48px] sm:min-h-0"
                  style={{ "--tw-ring-color": primaryColor } as React.CSSProperties}
                  disabled={submitState === "submitting"}
                />
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {errorMessage}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitState === "submitting"}
                className="w-full py-4 sm:py-3 px-5 rounded-lg font-semibold text-white text-base active:opacity-80 disabled:opacity-50 flex items-center justify-center gap-2 min-h-[52px] sm:min-h-0 mt-2"
                style={{
                  backgroundColor: primaryColor,
                  touchAction: "manipulation",
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {submitState === "submitting" ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Joining waitlist...
                  </>
                ) : (
                  <>
                    <Bell className="w-5 h-5" />
                    Join Waitlist
                  </>
                )}
              </button>

              <p className="text-center text-xs text-gray-500">
                We'll email everyone on the waitlist when a spot opens up. First to sign up gets the spot.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
